<template>
  <div class="w-100" data-cy="company-table-row">
    <b-col
      md="12"
      xl="12"
      lg="12"
      class="px-0 py-2 admin-company-column cy-active-company-list"
    >
      <b-card
        :class="{ active: isActive }"
        no-body
        @mouseenter="isActive = true"
        @mouseleave="isActive = false"
      >
        <b-card-body class="py-4 d-flex align-items-center">
          <b-col
            md="3"
            name="company-column"
            class="cy-current-customer-details d-flex align-items-center"
          >
            <b-avatar
              v-if="logo"
              data-cy="company-row-field-avatar"
              size="4.4rem"
              :src="logo"
            ></b-avatar>
            <b-avatar
              v-else
              data-cy="company-row-field-initials"
              class="customer-avatar bg--primary"
              size="4.4rem"
              :text="getInitialsFromName(name)"
            ></b-avatar>

            <h6
              :title="name"
              class="
                font-sm
                cy-current-customer-name
                font-family-medium
                primary--text
                reduce-line-height
                pl-3
              "
              data-cy="company-row-field-name"
            >
              {{ ellipsify(name, 30) }}
            </h6>
          </b-col>

          <b-col md="2" name="headroom-entity-column">
            <h6
              class="
                font-sm
                primary--text
                font-weight-normal
                text-capitalize
                cy-current-company-sub
              "
              data-cy="company-row-field-headroom-entity"
            >
              {{ headroomEntityLabel }}
            </h6>
          </b-col>

          <b-col md="4" name="membership-column">
            <ValidationProvider name="Subscription" rules="required">
              <b-form-group>
                <b-form-select
                  v-model="membershipId"
                  :key="membershipDropdownKey"
                  :options="membershipOptions"
                  data-cy="company-row-field-membership"
                  @change="promptUpdateSubscription($event, id)"
                ></b-form-select>

                <h6
                  v-if="!headroomEntity"
                  class="font-sm font-family-regular position-absolute danger"
                >
                  This is a required field
                </h6>
              </b-form-group>
            </ValidationProvider>
          </b-col>

          <b-col md="1" name="total-hours-column">
            <h6
              data-cy="company-row-field-total-hours"
              class="
                font-sm
                primary--text
                font-weight-normal
                text-center
                cy-current-company-sub
              "
            >
              {{ getTotalHoursLabel(companyMembership.totalHours) }}
            </h6>
          </b-col>

          <b-col
            cols="1"
            name="number-of-customers-column"
            class="align-items-center"
          >
            <h6
              class="
                font-sm
                cy-current-company
                primary--text
                font-weight-normal
                text-center
                reduce-line-height
              "
              @click="emitGoToCustomers"
            >
              <b-link
                class="d-inline-block w-50 hover-bg-highlight"
                title="Click to see customers."
                data-cy="company-row-field-customer-count"
                >{{ totalUsers || 0 }}</b-link
              >
            </h6>
          </b-col>

          <b-col
            cols="1"
            name="actions-column"
            class="d-flex position-relative justify-space-evenly"
            data-cy="company-row-field-actions"
          >
            <div
              v-if="companyIsActive"
              class="cy-current-company-archive d-flex justify-space-evenly"
            >
              <EditButton
                role="button"
                tabindex="0"
                class="mr-2"
                data-cy="company-row-field-actions-edit"
                @click="emitEdit(id)"
              />
              <ArchiveButton
                role="button"
                tabindex="0"
                class="ml-2"
                data-cy="company-row-field-actions-archive"
                @click="emitArchive(id)"
              />
            </div>
            <div
              v-else
              class="cy-current-company-archive d-flex justify-space-evenly"
            >
              <UnArchiveButton
                role="button"
                tabindex="0"
                class="ml-2"
                data-cy="company-row-field-actions-unarchive"
                @click="emitUnArchive(id)"
              />
              <DeleteButton
                role="button"
                tabindex="0"
                class="ml-2"
                data-cy="company-row-field-actions-delete"
                @click="emitDelete(id)"
              />
            </div>
          </b-col>
        </b-card-body>
      </b-card>
    </b-col>

    <ChangeSubscriptionModal
      ref="subModal"
      v-if="id"
      :id="id"
      :name="name"
      :logo="logo"
      :error="subscriptionError"
      :can-close="canCloseSubModal"
      @update-confirmed="confirmUpdateSubscription"
      @update-cancelled="cancelSubscriptionUpdate"
    />
  </div>
</template>

<script>
import { ValidationProvider } from "vee-validate/dist/vee-validate.full";

import { getInitialsFromName, ellipsify } from "@/utils/utils";

import EditButton from "./EditButton.vue";
import ArchiveButton from "./ArchiveButton.vue";
import UnArchiveButton from "./UnArchiveButton.vue";
import ChangeSubscriptionModal from "./ChangeSubscriptionModal.vue";
import DeleteButton from "./DeleteButton.vue";

export default {
  props: {
    id: { type: String },
    name: { type: String },
    logo: { type: String },
    headroomEntity: { type: String },
    email: { type: String },
    companyMembership: { type: Object },
    membershipLabel: { type: String },
    totalUsers: { type: Number },
    isArchived: { type: Boolean },
    memberships: { type: Array },
    subscriptionError: { type: String },
    canCloseSubModal: { type: Boolean }
  },

  components: {
    ArchiveButton,
    UnArchiveButton,
    EditButton,
    ChangeSubscriptionModal,
    ValidationProvider,
    DeleteButton
  },

  data() {
    return {
      isActive: false,

      prevMembershipId: "",
      prevMembershipLabel: "",
      membershipId: "",
      membershipDropdownKey: null
    };
  },

  created() {
    this.prevMembershipId = this.companyMembership.membershipDetails.id;
    this.prevMembershipLabel = this.membershipLabel;
    this.membershipId = this.companyMembership.membershipDetails.id;
  },

  computed: {
    headroomEntityLabel() {
      const entity = [
        { value: "EU", text: "Public Support Assistance BV (EU)" },
        { value: "US", text: "Public Support Assistance Services LLC (US)" }
      ].find(({ value }) => value === this.headroomEntity);

      return entity?.text;
    },

    companyIsActive() {
      return !this.isArchived;
    },

    membershipOptions() {
      const options = this.memberships || [];
      const current = this.companyMembership?.membershipDetails;
      if (!current?.id) {
        return options;
      }

      const alreadyListed = options.some(({ value }) => value === current.id);
      if (alreadyListed) {
        return options;
      }

      return [
        ...options,
        {
          value: current.id,
          text: this.membershipLabel,
          allowedFlexibleHours: current.allowedFlexibleHours,
          maxSeats: current.maxSeats
        }
      ];
    }
  },

  methods: {
    ellipsify,
    getInitialsFromName,
    trackSubscriptionChangeModalOpen() {
      this.$gtag.event("Company - Subscription Change Modal Open", {
        event_category: "feature"
      });
    },
    trackCompanyUpdateModalOpen() {
      this.$gtag.event("Company - Update Modal Open", {
        event_category: "feature"
      });
    },
    trackCompanyArchiveModalOpen() {
      this.$gtag.event("Company - Archive Modal Open", {
        event_category: "feature"
      });
    },
    trackCompanyUnArchiveModalOpen() {
      this.$gtag.event("Company - Archive Modal Open", {
        event_category: "feature"
      });
    },
    emitEdit(id) {
      this.trackCompanyUpdateModalOpen();
      this.$emit("edit-company", id);
    },

    emitArchive(id) {
      this.trackCompanyArchiveModalOpen();
      this.$emit("archive-company", id);
    },

    emitUnArchive(id) {
      this.trackCompanyUnArchiveModalOpen();
      this.$emit("unarchive-company", id);
    },

    async promptUpdateSubscription(updateMembershipId) {
      this.trackSubscriptionChangeModalOpen();
      const updateMembershipLabel = this.getUpdateMembershipLabel(
        updateMembershipId
      );
      const allowedFlexibleHours = this.checkIfIsFlexibleHours(
        updateMembershipId
      );

      await this.$refs.subModal.open(
        this.prevMembershipId,
        this.prevMembershipLabel,
        updateMembershipLabel,
        updateMembershipId,
        allowedFlexibleHours
      );
    },

    confirmUpdateSubscription(companyId, id, label, hoursNeeded = null) {
      // * We keep the latest value that was set before the current change
      // * Current change here refers to the `promptUpdateSubscription` method
      // * being called with the latest `title` value.
      this.prevMembershipId = id;
      this.prevMembershipLabel = label;

      const data = { data: { id: companyId, new_membership: id } };
      if (hoursNeeded) {
        data.data.total_hours = hoursNeeded;
      }

      this.$emit("confirm-update-subscription", data);
    },

    cancelSubscriptionUpdate() {
      this.prevMembershipLabel = this.companyMembership.membershipDetails.label;
      this.membershipId = this.companyMembership.membershipDetails.id;
      this.$emit("cancel-update-subscription");
    },

    getUpdateMembershipLabel(membershipId) {
      return this.membershipOptions.find(({ value }) => value === membershipId)
        ?.text;
    },

    getTotalHoursLabel(totalHours) {
      return totalHours > 0 ? totalHours : "Unlimited";
    },

    checkIfIsFlexibleHours(membershipId) {
      return this.membershipOptions.find(({ value }) => value === membershipId)
        ?.allowedFlexibleHours;
    },

    emitGoToCustomers() {
      this.$emit("go-to-customers");
    },

    emitDelete(id) {
      this.$emit("delete-company", id);
    }
  }
};
</script>

<style scoped>
.active {
  background-color: #f5f7fb;
  transition: 0.2s ease-out;
  transform: scale(1.005);
}

.hover-bg-highlight:hover {
  background-color: #e0edfb;
  border-radius: 0.5rem;
  transition: 0.1s ease-out;
}
</style>